import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { createToken } from "../../api/Tokens"; 
import { getAllInternalCustomers } from "../../api/Customers"; 
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import TokenForm from "../../components/token/TokenForm";
import ConfirmationModal from "../../components/ConfirmationModal";
import { isMaxEnergyKwhInvalid, normalizeMaxEnergyKwh } from "../../helpers/TokenValidity";
import { useTranslation } from "react-i18next";

const CreateTokenPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { customerPublicId } = location.state || {};
  const { getAccessToken } = useAuth();
  const [customers, setCustomers] = useState([]);
  const navigate = useNavigate();
  const [loading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });

  const [token, setToken] = useState({
    tagToken: "",
    tokenType: "RFID",
    name: "",
    customerPublicId: customerPublicId ? customerPublicId : "",
    expiredDateTimeUtc: new Date(
          new Date().setFullYear(new Date().getFullYear() + 5)
      ).toISOString(),
    authorizationStatus: "Accepted",
    isValid: true,
    tokenVehicleInfo: {},
  });

  useEffect(() => {
    handleGetCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

    const handleInputChange = (key, value) => {
        setHasChanges(true);
        setToken((prev) => ({ ...prev, [key]: value }));
    };

     const normalizeVehicleInfoForApi = (vehicleInfo = {}) => ({
        vehicleBrand: vehicleInfo.vehicleBrand ?? "",
        vehicleModel: vehicleInfo.vehicleModel ?? "",
        vehicleType: vehicleInfo.vehicleType ?? "",
        registrationNumber: vehicleInfo.registrationNumber ?? "",
        driverName: vehicleInfo.driverName ?? "",
        driverPhoneNumber: vehicleInfo.driverPhoneNumber ?? "",
    });

  const handleCreateToken = async () => {
    const toastId = toast.loading(t("toast.creating", { entity: t("entity.token") }));

    const requiredFields = [
      "name",
      "customerPublicId",
      "tagToken",
      "tokenType",
      "authorizationStatus",
    ];

    const missingFields = requiredFields.filter((field) => {
      const value = token[field];
      return value === "" || value === undefined || value === null;
    });

    setInvalidFields(missingFields);

    if (!token.name || token.name.length > 50) {
      setInvalidFields((prev) => [...prev, "name"]);
      toast.error(t("common.nameLength"));
      return;
    }

    if (isMaxEnergyKwhInvalid(token.maxEnergyKwh)) {
      toast.error(t("token.maxEnergyInvalid"), { id: toastId });
      return;
    }
    const maxEnergyKwh = normalizeMaxEnergyKwh(token.maxEnergyKwh);

    if (missingFields.length > 0) {
      toast.error(
        t("common.pleaseFillRequiredFields", { fields: missingFields.join(", ") }),
        {
          id: toastId,
        }
      );
      return;
    }

    try {
      const accessToken = await getAccessToken();

      if (!token.expiredDateTimeUtc || token.expiredDateTimeUtc === "") {
        token.expiredDateTimeUtc = null;
      }
        const payload = {
            ...token,
            maxEnergyKwh,
            tokenVehicleInfo: normalizeVehicleInfoForApi(token.tokenVehicleInfo),
        };

        const newToken = await createToken(payload, accessToken);
      toast.success(t("toast.created", { entity: t("entity.token") }), { id: toastId });

      if (customerPublicId) {
        navigate(`/token/${newToken.idTagPublicId}`, {
          state: { customerPublicId: customerPublicId },
        });
      } else {
        navigate(`/token/${newToken.idTagPublicId}`);
      }
    } catch (error) {
        toast.error(error?.message || t("toast.failedCreate", { entity: t("entity.token") }), {
        id: toastId,
      });
    }
  };

  const handleGetCustomers = async () => {
    try {
      const accessToken = await getAccessToken();
      const newCustomers = await getAllInternalCustomers(accessToken);
      setCustomers(newCustomers);
    } catch (error) {
      console.log(error);
    }
  };

  /*   const navigateBack = () => {
    if(customerPublicId) {
      navigate(`/customer/${customerPublicId}?tab=tokens`);
    } else {
      if (navigate.length > 1) {
        navigate(-1);
      } else {
        navigate("/tokens");
      }
    } 
  } */

  const handleBackClick = () => {
    if (hasChanges && customerPublicId) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: t("confirm.discard"),
        confirm: () => {
          navigate(`/customer/${customerPublicId}?tab=tokens`);
        },
      }));
    } else {
      if (navigate.length > 1) {
        navigate(-1);
      } else {
        navigate("/tokens");
      }
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute inset-0 pointer-events-none">
            <LoadingTablePage title={t("pages.chargingTokens")} loading={loading} />
          </div>

          <div className={`${loading ? "loading" : ""} page-content`}>
            <div className="flex flex-row items-center pb-4">
              <button
                onClick={() => handleBackClick()}
                className="text-black flex"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4 ">{t("pages.createToken")}</h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => handleBackClick()}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!token?.name && !hasChanges}
                  onClick={handleCreateToken}
                >
                  {t("common.save")}
                </button>
              </div>
            </div>

            <div className=" px-4 py-8 shadow-lg rounded-md w-full">
              <div className="grid sm:-cols-1 gap-4">
                <TokenForm
                  token={token}
                  handleInputChange={handleInputChange}
                  customers={customers}
                  invalidFields={invalidFields}
                  setInvalidFields={setInvalidFields}
                />
              </div>
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={
            confirmState.title
              ? confirmState.title
              : t("confirm.deleteThirdPartyRef")
          }
          onClose={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
          }}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default CreateTokenPage;
